// vim: set ts=2 sts=2 sw=2 et:
//
// This file is part of OpenLifter, simple Powerlifting meet software.
// Copyright (C) 2019 The OpenPowerlifting Project.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU Affero General Public License as
// published by the Free Software Foundation, either version 3 of the
// License, or (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU Affero General Public License for more details.
//
// You should have received a copy of the GNU Affero General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.

// The RootContainer manually implements route parsing from GET parameters.
//
// The issue is that because OpenLifter is statically hosted, only the base
// "/" route actually contains the index.html of the application. The other
// routes (such as "/lifting") have index.html files that redirect to "/".
//
// We've changed things so that "/lifting/index.html" redirects to "/?lifting".
// But React Router isn't able to route based on GET parameters.
//
// Therefore it's set up so that "/" routes to this RootContainer,
// and the RootContainer inspects the GET parameters and renders the
// appropriate container.

import React from "react";
import { useLocation } from "react-router-dom";

import HomeContainer from "./HomeContainer";
import MeetSetupContainer from "./MeetSetupContainer";
import RegistrationContainer from "./RegistrationContainer";
import WeighinsContainer from "./WeighinsContainer";
import FlightOrderContainer from "./FlightOrderContainer";
import ResultsContainer from "./ResultsContainer";
import DebugContainer from "./DebugContainer";
import AboutContainer from "./AboutContainer";
import LiftingContainer from "./LiftingContainer";
import ClassicLiftingLiveContainer from "../wrpf/operation/ClassicLiftingLiveContainer";
import { OperatorLiveHostContext } from "../wrpf/operation/OperatorLiveHost";
import RecordsRoute from "../wrpf/records/RecordsRoute";

interface Props {
  locationSearch: string;
  /** OperatorLiveHost ya muestra `?lifting` (y lo mantiene vivo entre pantallas). */
  liftingHostedOutside?: boolean;
}

class RootContainerView extends React.Component<Props> {
  render() {
    if (typeof this.props.locationSearch !== "string") {
      return <HomeContainer />;
    }

    const params = new URLSearchParams(this.props.locationSearch);

    if (params.has("meet-setup")) {
      return <MeetSetupContainer />;
    }
    if (params.has("registration")) {
      return <RegistrationContainer />;
    }
    if (params.has("weigh-ins")) {
      return <WeighinsContainer />;
    }
    if (params.has("flight-order")) {
      return <FlightOrderContainer />;
    }
    if (params.has("classic-lifting")) {
      return <LiftingContainer />;
    }
    if (params.has("lifting")) {
      if (this.props.liftingHostedOutside === true) {
        return null;
      }
      return <ClassicLiftingLiveContainer locationSearch={this.props.locationSearch} />;
    }
    if (params.has("results")) {
      return <ResultsContainer />;
    }
    if (params.has("records")) {
      return <RecordsRoute />;
    }
    if (params.has("debug")) {
      return <DebugContainer />;
    }
    if (params.has("about")) {
      return <AboutContainer />;
    }

    return <HomeContainer />;
  }
}

const RootContainer = () => {
  const location = useLocation();
  const liftingHostedOutside = React.useContext(OperatorLiveHostContext);
  return <RootContainerView locationSearch={location.search} liftingHostedOutside={liftingHostedOutside} />;
};

export default RootContainer;
